'use client';

import React, { useState, useMemo } from 'react';

// ─── TYPES ────────────────────────────────────────────────────────────────────
interface ResourceItem {
  name: string;
  category: string;
  nameOfAllah: string;
  arabicName: string;
  meaning: string;
  address: string;
  contact: string;
  desc: string;
}

interface Ward {
  id: string;
  name: string;
  shortName: string;
  severity: 'CRITICAL' | 'HIGH' | 'MODERATE';
  indicators: { poverty: number; medianIncome: number; foodDesert: number; unemployment: number; uninsured: number; vacancy: number };
  masajid: string[];
  anchors: string[];
  resources: ResourceItem[];
  history: string;
  openLetterQuote: string;
  disparities: { name: string; value: string; nameOfAllah: string; arabicName: string; meaning: string; islahCall: string }[];
  pivotalMoments: { year: string; event: string }[];
  irlLens: { stage: string; arabic: string; title: string; content: string }[];
  context: { nameOfAllah: string; arabicName: string; meaning: string; ayat: string; ayatRef: string; ayatTrans: string; intro: string; body: string };
}

// ─── RESOURCE CATEGORIES (for Resources tab header) ──────────────────────────
const RESOURCE_CATEGORIES: Record<string, { nameOfAllah: string; arabicName: string; meaning: string; ayat: string; ayatRef: string; ayatTrans: string; intro: string }> = {
  Food: {
    nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider',
    ayat: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ إِلَّا عَلَى اللَّهِ رِزْقُهَا',
    ayatRef: 'Quran 11:6',
    ayatTrans: '"There is no creature on earth whose provision is not guaranteed by Allah."',
    intro: 'Food deserts are not natural phenomena — they are the accumulated weight of redlining, disinvestment, and structural neglect. The Rizq of Allah exists in the earth; the Amanah is to trace where the barriers are and bridge to Islah. Every food pantry, community garden, and halal distribution center is a point of Uboor — a bridge from disparity to provision. Fisabilillah.',
  },
  Health: {
    nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer',
    ayat: 'وَإِذَا مَرِضْتُ فَهُوَ يَشْفِينِ',
    ayatRef: 'Quran 26:80',
    ayatTrans: '"And when I am ill, it is He who cures me."',
    intro: 'Healthcare deserts follow the same boundaries as food deserts and redlining maps. The uninsured rate in these communities is not a personal failure — it is a structural one. The Amanah of Ash-Shafi is to bridge the sick to healing, to advocate for health equity, and to recognize that every free clinic and community health worker is an instrument of divine mercy. Fisabilillah.',
  },
  Education: {
    nameOfAllah: 'AL-ALIM', arabicName: 'الْعَلِيم', meaning: 'The All-Knowing',
    ayat: 'اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ',
    ayatRef: 'Quran 96:1',
    ayatTrans: '"Read in the name of your Lord who created — taught by the pen, taught humanity what it did not know."',
    intro: 'The first command of revelation was to read. Educational disinvestment in these communities is a direct transgression against this command. The Amanah of Al-Alim is to bridge every child, adult, and returning citizen to knowledge — Islamic schools, GED programs, literacy networks, and vocational training are all expressions of this divine trust. Fisabilillah.',
  },
  Housing: {
    nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend',
    ayat: 'وَاللَّهُ جَعَلَ لَكُم مِّن بُيُوتِكُمْ سَكَنًا',
    ayatRef: 'Quran 16:80',
    ayatTrans: '"Allah has given you homes as places of rest."',
    intro: 'Displacement is violence. Gentrification without community benefit is a structural transgression against the Amanah of shelter. The Amanah of Al-Wali is to protect communities from displacement, to preserve affordable housing, and to bridge caretakers to anti-displacement resources, land trusts, and tenant rights organizations. Fisabilillah.',
  },
  Income: {
    nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher',
    ayat: 'وَاسْأَلُوا اللَّهَ مِن فَضْلِهِ',
    ayatRef: 'Quran 4:32',
    ayatTrans: '"Ask Allah for His bounty. Indeed, Allah is ever Knowing of all things."',
    intro: 'Income disparities in these communities are the direct legacy of wage theft, redlining, and structural exclusion from wealth-building. The Amanah of Al-Mughni is to bridge caretakers to economic empowerment — workforce development, small business support, and financial literacy are instruments of Islah. Fisabilillah.',
  },
  Reentry: {
    nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ', meaning: 'The Pardoner',
    ayat: 'قُلْ يَا عِبَادِيَ الَّذِينَ أَسْرَفُوا عَلَىٰ أَنفُسِهِمْ لَا تَقْنَطُوا مِن رَّحْمَةِ اللَّهِ',
    ayatRef: 'Quran 39:53',
    ayatTrans: '"Say: O My servants who have transgressed against themselves, do not despair of the mercy of Allah."',
    intro: 'Mass incarceration has devastated these communities. The returning citizen carries the weight of a system designed to recycle, not rehabilitate. The Amanah of Al-Afuww is to receive the returning citizen with mercy, to bridge them to vocational training, transitional housing, and community reintegration — recognizing that every human being carries the capacity for Islah. Fisabilillah.',
  },
  Justice: {
    nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable',
    ayat: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ شُهَدَاءَ لِلَّهِ',
    ayatRef: 'Quran 4:135',
    ayatTrans: '"Be persistently standing firm in justice, witnesses for Allah, even if it be against yourselves."',
    intro: 'The redlining map and the food desert map are the same map. Structural injustice is not abstract — it has addresses, zip codes, and ward boundaries. The Amanah of Al-Muqsit is to name the injustice, to document it, and to bridge caretakers to legal aid, civil rights organizations, and advocacy networks that pursue Islah through justice. Fisabilillah.',
  },
  Community: {
    nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving',
    ayat: 'إِنَّمَا الْمُؤْمِنُونَ إِخْوَةٌ',
    ayatRef: 'Quran 49:10',
    ayatTrans: '"The believers are but brothers, so make peace between your brothers and fear Allah."',
    intro: 'Community is the infrastructure of Islah. Masajid, community centers, youth programs, and cultural organizations are the connective tissue that holds neighborhoods together under pressure. The Amanah of Al-Wadud is to strengthen these bonds, to bridge isolated individuals to community, and to recognize that every gathering fisabilillah is an act of worship. Fisabilillah.',
  },
};

// ─── WARD DATA ────────────────────────────────────────────────────────────────
const CHICAGO_WARDS: Ward[] = [
  // ── BRONZEVILLE (Primary Ward) ──────────────────────────────────────────────
  {
    id: 'bronzeville',
    name: 'Bronzeville',
    shortName: 'BRONZEVILLE',
    severity: 'HIGH',
    indicators: { poverty: 28, medianIncome: 38000, foodDesert: 62, unemployment: 18, uninsured: 24, vacancy: 30 },
    masajid: ['Masjid Al-Ihsan (321 E Pershing Rd)', 'Islamic Institute of Urban Affairs'],
    anchors: ['Masjid Al-Ihsan', 'DuSable Black History Museum', 'Bronzeville Community Market', 'Provident Hospital'],
    openLetterQuote: 'Bronzeville is the Black Metropolis — the cultural and spiritual heart of Black Chicago. The Amanah of this place is immense: to hold the legacy of what was built and to bridge it to what must be built now.',
    pivotalMoments: [
      { year: '1890s–1920s', event: 'Great Migration first wave — Bronzeville becomes the "Black Metropolis," home to 300,000 Black Americans' },
      { year: '1919', event: 'Chicago Race Riot — Black residents defend Bronzeville against white mob violence' },
      { year: '1920s–40s', event: 'Bronzeville at its peak: Black-owned businesses, jazz clubs, newspapers, churches, and civic institutions' },
      { year: '1940', event: 'HOLC redlining maps designate Bronzeville as "hazardous" — systematic mortgage denial begins' },
      { year: '1960s', event: 'Urban renewal and Robert Taylor Homes construction displaces thousands; community fabric torn' },
      { year: '1990s', event: 'Robert Taylor Homes demolition begins; displacement of 25,000 residents' },
      { year: '2000s', event: 'Gentrification pressure from the north; Muslim community roots deepen under Imam Madyun' },
      { year: 'Present', event: 'Masjid Al-Ihsan anchors the Muslim community in Bronzeville — a living Amanah' },
    ],
    disparities: [
      { name: 'Food Desert', value: '62%', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', islahCall: 'Bronzeville\'s food desert is a legacy of disinvestment. Bridge to Bronzeville Community Market, Greater Chicago Food Depository, and IMAN food programs.' },
      { name: 'Poverty Rate', value: '28%', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', islahCall: 'Economic disinvestment requires economic Islah. Bridge to Chicago Urban League, workforce development, and small business support.' },
      { name: 'Displacement Pressure', value: 'Rising gentrification', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', islahCall: 'Bronzeville faces gentrification from the north. Bridge to Metropolitan Tenants Organization and NHS Chicago.' },
      { name: 'Uninsured', value: '24%', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', islahCall: 'Healthcare access is a right. Bridge to IMAN Health Clinic, Mercy Hospital, and Access Community Health Network.' },
    ],
    resources: [
      { name: 'Masjid Al-Ihsan — Community Programs', category: 'Community', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', address: '321 E Pershing Rd, Chicago, IL 60653', contact: 'masjidihsan.org', desc: 'Daily Salah, two Jumu\'ah prayers (1:15pm & 2:15pm), Eid, Zakaat, lectures, Bonds to Brotherhood, Quran Halaqas, and community support. Anchor institution of Bronzeville\'s Muslim community under Imam Abdullah Madyun.' },
      { name: 'Masjid Al-Ihsan — Sunday Feeding Program', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '321 E Pershing Rd, Chicago, IL 60653', contact: 'masjidihsan.org', desc: 'Every Sunday, Masjid Al-Ihsan serves a community meal to all who come. No one is turned away. A living expression of Ar-Razzaq — The Ever-Provider — in the heart of Bronzeville.' },
      { name: 'Masjid Al-Ihsan — Iftar on Wheels', category: 'Food', nameOfAllah: 'AL-KARIM', arabicName: 'الْكَرِيم', meaning: 'The Most Generous', address: 'Bronzeville & surrounding areas (mobile)', contact: 'masjidihsan.org', desc: 'During Ramadan, Masjid Al-Ihsan delivers hot iftar meals to those who cannot come to the Masjid — serving Bronzeville and surrounding neighborhoods with mobile iftar distribution.' },
      { name: 'Downtown Islamic Center — Sunday Morning Feeding', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '218 S Wabash Ave, Chicago, IL 60604', contact: 'downtownislamiccenter.com', desc: 'The Downtown Islamic Center\'s Sunday morning feeding program serves the community with a free meal every Sunday morning. Open to all. A bridge from the Masjid to the street.' },
      { name: 'Masjid Al-Farooq', category: 'Community', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', address: 'Chicago, IL', contact: 'Contact for address', desc: 'Masjid Al-Farooq serves the Muslim community of Chicago with daily prayers, Jumu\'ah, Islamic education, and community programs. A sister institution in the network of Chicago\'s South Side masajid.' },
      { name: 'Bronzeville Community Market', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '3500 S King Dr, Chicago, IL 60616', contact: 'bronzevillecommunity.org', desc: 'Fresh produce and community food access in the heart of Bronzeville. Addresses food desert conditions directly.' },
      { name: 'Greater Chicago Food Depository', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '4100 W Ann Lurie Pl, Chicago', contact: '(773) 247-3663', desc: 'Chicago\'s food bank. Mobile distribution in Bronzeville food desert areas. Partners with 700+ pantries.' },
      { name: 'IMAN Health Clinic', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: '2744 W 63rd St, Chicago', contact: '(773) 434-4626', desc: 'Free community health clinic. Serves uninsured patients across Chicago\'s south side including Bronzeville.' },
      { name: 'Mercy Hospital & Medical Center', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: '2525 S Michigan Ave, Chicago, IL 60616', contact: '(312) 567-2000', desc: 'Community hospital serving Bronzeville and surrounding areas. Emergency and primary care.' },
      { name: 'Chicago Urban League', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: '4510 S Michigan Ave, Chicago, IL 60653', contact: '(773) 285-5800', desc: 'Economic empowerment, workforce development, and entrepreneurship programs. Located in Bronzeville.' },
      { name: 'Metropolitan Tenants Organization', category: 'Housing', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', address: '1 N Dearborn St, Chicago', contact: '(773) 292-4988', desc: 'Tenant rights advocacy, eviction defense, and housing stability services for Chicago renters.' },
      { name: 'CAIR Chicago', category: 'Justice', nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable', address: '17 N State St, Chicago', contact: '(312) 212-1520', desc: 'Civil rights advocacy, legal services, and community empowerment for Chicago\'s Muslim community.' },
      { name: 'Cabrini Green Legal Aid', category: 'Reentry', nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ', meaning: 'The Pardoner', address: '6 W Hubbard St, Chicago', contact: '(312) 738-2452', desc: 'Free legal representation for people with criminal records seeking to clear records, access housing, and restore rights.' },
      { name: 'DuSable Black History Museum', category: 'Education', nameOfAllah: 'AL-ALIM', arabicName: 'الْعَلِيم', meaning: 'The All-Knowing', address: '740 E 56th Pl, Chicago, IL 60637', contact: '(773) 947-0600', desc: 'Premier institution of Black history and culture. Educational programs, archives, and community events.' },
      { name: 'ICNA Relief Chicago', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: 'Chicago Metro Area', contact: 'icnarelief.org', desc: 'Emergency financial assistance, Zakat distribution, and economic support for Muslim families.' },
      { name: 'Safer Foundation', category: 'Reentry', nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ', meaning: 'The Pardoner', address: '571 W Jackson Blvd, Chicago', contact: '(312) 922-2200', desc: 'Comprehensive reentry services: employment, housing, education, and community support for returning citizens.' },
    ],
    history: 'Bronzeville — the Black Metropolis — is one of the most historically significant African American communities in the United States. During the Great Migration (1910s–1940s), it became home to 300,000 Black Americans who built a self-sufficient city within a city: Black-owned banks, newspapers (Chicago Defender), jazz clubs, churches, and civic institutions. The 1940 HOLC redlining maps designated Bronzeville as "hazardous," systematically denying mortgage access and ensuring disinvestment. Urban renewal in the 1960s and the construction of the Robert Taylor Homes displaced thousands. The demolition of the projects in the 1990s–2000s displaced 25,000 more. Today, Masjid Al-Ihsan under Imam Abdullah Madyun anchors the Muslim community in Bronzeville — a living Amanah in the heart of the Black Metropolis.',
    irlLens: [
      { stage: 'ENCOUNTER', arabic: 'مُشَاهَدَة', title: 'Sensory Contact with Bronzeville', content: 'Walking 35th and King Drive — the Bronzeville Walk of Fame bronze plaques embedded in the sidewalk. The DuSable Museum. The Provident Hospital where Daniel Hale Williams performed the first open-heart surgery. The vacant lots where the Robert Taylor Homes once stood. The Masjid on Pershing Road. What do you see? What has been lost? What remains? What is being built?' },
      { stage: 'EXAMINATION', arabic: 'فَحْص', title: 'Three Questions of the Lens', content: 'What is the haal of this place? The Black Metropolis built in defiance of segregation — now facing displacement from gentrification. The food desert that maps onto the 1940 HOLC redlining boundary. The Muslim community that chose to root here. What Names of Allah are visible in what remains? What Names are called for in what is absent?' },
      { stage: 'RECOGNITION', arabic: 'مَعْرِفَة', title: 'Names Visible in Bronzeville', content: 'Ar-Razzaq — in the Bronzeville Community Market, in the food programs of Masjid Al-Ihsan. Al-Alim — in the DuSable Museum, in the Chicago Defender archives, in the Islamic Institute of Urban Affairs. Al-Wadud — in the community that gathered around Imam Madyun. Al-Wali — in the tenants fighting displacement. Al-Muqsit — in the civil rights legacy of this neighborhood.' },
      { stage: 'UBOOR', arabic: 'عُبُور', title: 'Crossing Over — Bronzeville Islah', content: 'The Amanah of Bronzeville is to hold the legacy of the Black Metropolis and to bridge it to the present. Every resource in this guide is a form of Uboor — a crossing from disparity to provision, from isolation to community, from injustice to Islah. Masjid Al-Ihsan is the anchor. The caretaker is the bridge. Fisabilillah.' },
    ],
    context: {
      nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving',
      ayat: 'وَهُوَ الْغَفُورُ الْوَدُودُ',
      ayatRef: 'Quran 85:14',
      ayatTrans: '"And He is the Forgiving, the Loving."',
      intro: 'Bronzeville is the Black Metropolis — the cultural and spiritual heart of Black Chicago. It was built by people who were told they did not belong, who built anyway, who created beauty and knowledge and community in the face of systematic exclusion.',
      body: 'The 1940 HOLC redlining maps designated Bronzeville as "hazardous" — a bureaucratic act of violence that denied mortgage access, ensured disinvestment, and set the conditions for the food deserts and poverty that persist today. Urban renewal in the 1960s and the construction of the Robert Taylor Homes displaced thousands. The demolition of the projects in the 1990s–2000s displaced 25,000 more. And yet — Bronzeville remains. Masjid Al-Ihsan under Imam Abdullah Madyun is the anchor of the Muslim community here. The Islamic Institute of Urban Affairs is the knowledge center. The Bronzeville Community Market is the provision point. The caretaker\'s work in Bronzeville is to hold what was built, to bridge what was broken, and to plant what must grow. Fisabilillah.',
    },
  },
  // ── ENGLEWOOD ────────────────────────────────────────────────────────────────
  {
    id: 'englewood',
    name: 'Englewood',
    shortName: 'ENGLEWOOD',
    severity: 'CRITICAL',
    indicators: { poverty: 45, medianIncome: 22000, foodDesert: 85, unemployment: 28, uninsured: 38, vacancy: 55 },
    masajid: ['IMAN Chicago HQ', 'Masjid Al-Taqwa'],
    anchors: ['IMAN Chicago HQ', 'Englewood Nature Trail', 'Kennedy-King College'],
    openLetterQuote: 'IMAN was born in Englewood — from the community, for the community. The Amanah is to be the roots that hold the soil.',
    pivotalMoments: [
      { year: '1919', event: 'Chicago Race Riot — Englewood among neighborhoods where Black residents faced violent displacement' },
      { year: '1940s–50s', event: 'Great Migration brings hundreds of thousands; Englewood becomes majority Black' },
      { year: '1940', event: 'HOLC redlining maps designate Englewood as "hazardous" — systematic mortgage denial begins' },
      { year: '1994', event: 'Inner-City Muslim Action Network (IMAN) founded in Englewood — anchor institution born' },
      { year: '2000s', event: 'Cabrini Green and public housing dispersal sends displaced families to Englewood' },
    ],
    disparities: [
      { name: 'Poverty Rate', value: '45%', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', islahCall: 'Englewood carries the full weight of structural disinvestment. Bridge to IMAN economic programs and workforce development.' },
      { name: 'Food Desert', value: '85%', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', islahCall: 'The Rizq exists — the barriers are structural. Bridge to IMAN food programs and Greater Chicago Food Depository.' },
      { name: 'Vacancy Rate', value: '55%', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', islahCall: 'Vacant land is a trust. Bridge to NHS Chicago and community land trust programs.' },
      { name: 'Unemployment', value: '28%', nameOfAllah: 'AL-WAHHAB', arabicName: 'الْوَهَّاب', meaning: 'The Bestower', islahCall: 'Employment is dignity. Bridge to IMAN Green ReEntry and Chicago Cook Workforce Partnership.' },
    ],
    resources: [
      { name: 'IMAN — Inner-City Muslim Action Network', category: 'Community', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', address: '2744 W 63rd St, Chicago', contact: '(773) 434-4626', desc: 'Founded 1994 in Englewood. Comprehensive community development: health clinic, Green ReEntry, arts, advocacy, food programs. Anchor institution.' },
      { name: 'IMAN Green ReEntry Chicago', category: 'Reentry', nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ', meaning: 'The Pardoner', address: '2744 W 63rd St, Chicago', contact: '(773) 434-4626', desc: 'NCCER-certified construction vocational training, transitional housing, and reintegration support for returning citizens.' },
      { name: 'IMAN Health Clinic', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: '2744 W 63rd St, Chicago', contact: '(773) 434-4626', desc: 'Free community health clinic. Serves uninsured patients. Integrated with IMAN\'s holistic community model.' },
      { name: 'Greater Chicago Food Depository', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '4100 W Ann Lurie Pl, Chicago', contact: '(773) 247-3663', desc: 'Chicago\'s food bank. Partners with 700+ pantries and programs. Mobile distribution in food desert areas.' },
      { name: 'Grow Greater Englewood', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: 'Englewood, Chicago', contact: 'growgreaterenglewood.org', desc: 'Urban agriculture and food justice initiative rooted in Englewood. Community gardens, food access, youth programs.' },
      { name: 'Neighborhood Housing Services Chicago', category: 'Housing', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', address: '1279 N Milwaukee Ave, Chicago', contact: '(773) 329-4010', desc: 'Affordable housing counseling, foreclosure prevention, and community development. Serves south and west side neighborhoods.' },
      { name: 'Chicago Cook Workforce Partnership', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: '69 W Washington St, Chicago', contact: '(312) 603-0200', desc: 'Workforce development, job training, and career services. American Job Centers throughout Chicago.' },
      { name: 'Safer Foundation', category: 'Reentry', nameOfAllah: 'AL-AFUWW', arabicName: 'الْعَفُوّ', meaning: 'The Pardoner', address: '571 W Jackson Blvd, Chicago', contact: '(312) 922-2200', desc: 'Comprehensive reentry services: employment, housing, education, and community support for returning citizens.' },
    ],
    history: 'Englewood\'s history is the history of Chicago\'s Black Belt — shaped by the Great Migration, redlining, and systematic disinvestment. The 1919 Chicago Race Riot established the violent boundaries of racial segregation. The 1940 HOLC maps designated Englewood as "hazardous," denying mortgage access and ensuring disinvestment. IMAN was founded in Englewood in 1994 — born from the community, for the community. The dispersal of Cabrini Green and other public housing projects in the 2000s sent displaced families to Englewood, deepening existing pressures.',
    irlLens: [
      { stage: 'ENCOUNTER', arabic: 'مُشَاهَدَة', title: 'Sensory Contact with Englewood', content: 'Walking 63rd Street — the IMAN headquarters, the community garden where a vacant lot once stood, the Kennedy-King College students, the Englewood Nature Trail carved from abandoned rail lines. What do you see? What has been built from nothing? What Names are visible in the work of IMAN?' },
      { stage: 'EXAMINATION', arabic: 'فَحْص', title: 'Three Questions of the Lens', content: 'What is the haal of Englewood? 45% poverty, 85% food desert, 55% vacancy — and yet IMAN, founded here in 1994, has built one of the most comprehensive community development organizations in the Muslim world. What does it mean that the anchor institution of Englewood was born from within it?' },
      { stage: 'RECOGNITION', arabic: 'مَعْرِفَة', title: 'Names Visible in Englewood', content: 'Al-Wadud — in IMAN\'s founding, in the community that gathered around it. Ar-Razzaq — in Grow Greater Englewood\'s urban farms. Ash-Shafi — in IMAN\'s free health clinic. Al-Afuww — in Green ReEntry, receiving returning citizens with mercy. Al-Alim — in Kennedy-King College, in the youth programs.' },
      { stage: 'UBOOR', arabic: 'عُبُور', title: 'Crossing Over — Englewood Islah', content: 'Englewood is where IMAN was born. The Amanah of this place is to be the roots that hold the soil — to build from within, to receive the displaced with mercy, to bridge the food desert to provision, the vacancy to community. Every resource here is a form of Uboor. Fisabilillah.' },
    ],
    context: {
      nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving',
      ayat: 'وَاعْتَصِمُوا بِحَبْلِ اللَّهِ جَمِيعًا وَلَا تَفَرَّقُوا',
      ayatRef: 'Quran 3:103',
      ayatTrans: '"And hold firmly to the rope of Allah all together and do not become divided."',
      intro: 'Englewood is where IMAN was born in 1994 — a community organization that grew from within the neighborhood it serves. This is the Amanah of Englewood: to build from within, to receive the displaced with mercy, to bridge disparity to provision.',
      body: 'The 1940 HOLC redlining maps designated Englewood as "hazardous." The 1919 Race Riot established the violent boundaries of segregation. The dispersal of public housing in the 2000s sent displaced families here, deepening existing pressures. And yet — IMAN. Founded in 1994 by young Muslims who chose to root themselves in Englewood, IMAN has built a comprehensive community development organization: free health clinic, Green ReEntry vocational training, urban agriculture, arts programming, and advocacy. The Amanah of Englewood is to be the roots that hold the soil. Fisabilillah.',
    },
  },
  // ── SOUTH SHORE ──────────────────────────────────────────────────────────────
  {
    id: 'south-shore',
    name: 'South Shore',
    shortName: 'S. SHORE',
    severity: 'CRITICAL',
    indicators: { poverty: 35, medianIncome: 28000, foodDesert: 78, unemployment: 22, uninsured: 30, vacancy: 38 },
    masajid: ['Masjid Al-Fatir', 'South Shore Islamic Center'],
    anchors: ['Masjid Al-Fatir', 'South Shore Cultural Center', 'Iftar on Wheels (IMAN)'],
    openLetterQuote: 'The lake is at the edge of this community — a reminder that Allah\'s Rizq surrounds us even in the midst of desert.',
    pivotalMoments: [
      { year: '1920s–40s', event: 'South Shore developed as Jewish and Irish middle-class neighborhood' },
      { year: '1960s–70s', event: 'Rapid racial transition as Black families move in; white flight and disinvestment follow' },
      { year: '1980s', event: 'Masjid Al-Fatir established — anchor of South Shore Muslim community' },
      { year: '2010s', event: 'Iftar on Wheels (IMAN) begins serving South Shore during Ramadan' },
    ],
    disparities: [
      { name: 'Food Desert', value: '78%', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', islahCall: 'The lake is nearby but the food desert is real. Bridge to Masjid Al-Fatir food programs and Greater Chicago Food Depository.' },
      { name: 'Displacement Pressure', value: '55% gentrification risk', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', islahCall: 'Lakefront development is pushing displacement. Bridge to NHS Chicago and Metropolitan Tenants Organization.' },
    ],
    resources: [
      { name: 'Masjid Al-Fatir Feed-the-People Program', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '7201 S Constance Ave, Chicago', contact: '(773) 363-0476', desc: 'Community food distribution program rooted in Masjid Al-Fatir. Serves South Shore and surrounding neighborhoods.' },
      { name: 'IMAN Iftar on Wheels', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: 'South Shore & Englewood (mobile)', contact: 'imancentral.org', desc: 'Mobile iftar distribution during Ramadan. Serves food desert areas across Chicago\'s south side.' },
      { name: 'Metropolitan Tenants Organization', category: 'Housing', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', address: '1 N Dearborn St, Chicago', contact: '(312) 292-4988', desc: 'Tenant rights advocacy, eviction defense, and housing stability services for Chicago renters.' },
      { name: 'IMAN — Inner-City Muslim Action Network', category: 'Community', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', address: '2744 W 63rd St, Chicago', contact: '(773) 434-4626', desc: 'Comprehensive community development serving south side Chicago. Health, reentry, food, arts, advocacy.' },
      { name: 'Erie Family Health Centers', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: 'Multiple Chicago locations', contact: '(312) 666-3494', desc: 'Federally qualified health center. Sliding scale fees. Serves uninsured and underinsured patients.' },
    ],
    history: 'South Shore developed as a middle-class Jewish and Irish neighborhood in the early 20th century. Rapid racial transition in the 1960s–70s brought Black families as white flight and disinvestment followed. Masjid Al-Fatir was established in the 1980s and became the anchor of the South Shore Muslim community. The neighborhood sits on Lake Michigan\'s shore — a geographic irony given the food desert conditions that persist despite the natural abundance nearby.',
    irlLens: [
      { stage: 'ENCOUNTER', arabic: 'مُشَاهَدَة', title: 'Sensory Contact with South Shore', content: 'Standing at the lakefront — Lake Michigan stretching to the horizon, the South Shore Cultural Center behind you, the food desert extending westward. The lake is Ar-Razzaq made visible. The food desert is the Amanah unfulfilled. What do you see in the gap between the two?' },
      { stage: 'EXAMINATION', arabic: 'فَحْص', title: 'Three Questions of the Lens', content: 'What is the haal of South Shore? The lake — abundance — at the eastern edge. The food desert — absence — extending westward. Masjid Al-Fatir as the anchor. IMAN\'s Iftar on Wheels as the bridge. What Names are visible in the work of those who bridge the gap?' },
      { stage: 'RECOGNITION', arabic: 'مَعْرِفَة', title: 'Names Visible in South Shore', content: 'Ar-Razzaq — in the lake, in Masjid Al-Fatir\'s food programs, in IMAN\'s Iftar on Wheels. Al-Wali — in the tenant rights advocates fighting lakefront displacement. Al-Wadud — in the community that gathered around Masjid Al-Fatir.' },
      { stage: 'UBOOR', arabic: 'عُبُور', title: 'Crossing Over — South Shore Islah', content: 'The Amanah of South Shore is to bridge the lake\'s abundance to the food desert\'s absence. Every food program, every tenant rights advocate, every community gathering is a form of Uboor. Fisabilillah.' },
    ],
    context: {
      nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider',
      ayat: 'وَفِي السَّمَاءِ رِزْقُكُمْ وَمَا تُوعَدُونَ',
      ayatRef: 'Quran 51:22',
      ayatTrans: '"And in the heaven is your provision and whatever you are promised."',
      intro: 'South Shore sits on the edge of Lake Michigan — one of the largest freshwater bodies on earth. The irony of a food desert adjacent to such abundance encodes the haal of this neighborhood.',
      body: 'The food desert that covers 78% of South Shore maps almost exactly onto the 1960s–70s disinvestment that followed racial transition. Masjid Al-Fatir, established in the 1980s, became the anchor of the Muslim community here. IMAN\'s Iftar on Wheels bridges the food desert during Ramadan. The caretaker\'s work in South Shore is to see the abundance that surrounds the community and to bridge the structural barriers that prevent access to it. Fisabilillah.',
    },
  },
  // ── AUSTIN ────────────────────────────────────────────────────────────────────
  {
    id: 'austin',
    name: 'Austin',
    shortName: 'AUSTIN',
    severity: 'CRITICAL',
    indicators: { poverty: 38, medianIncome: 25000, foodDesert: 82, unemployment: 25, uninsured: 35, vacancy: 48 },
    masajid: ['Masjid Al-Taqwa (Austin)', 'Austin Islamic Center'],
    anchors: ['Austin Town Hall', 'Malcolm X College (nearby)', 'Austin Polytech High School'],
    openLetterQuote: 'Austin is the largest neighborhood in Chicago — its haal carries the weight of the entire west side.',
    pivotalMoments: [
      { year: '1940s–60s', event: 'Great Migration transforms Austin from white working-class to majority Black neighborhood' },
      { year: '1968', event: 'Martin Luther King Jr. assassination triggers west side uprising; Austin devastated' },
      { year: '1970s–80s', event: 'Highest vacancy rate on Chicago\'s west side develops as disinvestment accelerates' },
    ],
    disparities: [
      { name: 'Food Desert', value: '82%', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', islahCall: 'Austin\'s food desert is among the most severe on the west side. Bridge to Greater Chicago Food Depository and community gardens.' },
      { name: 'Vacancy Rate', value: '48%', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', islahCall: 'Highest vacancy on the west side. Bridge to NHS Chicago and community land trust programs.' },
    ],
    resources: [
      { name: 'Greater Chicago Food Depository', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '4100 W Ann Lurie Pl, Chicago', contact: '(773) 247-3663', desc: 'Chicago\'s food bank. Mobile distribution in Austin food desert areas.' },
      { name: 'Cara Collective', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: '700 N Sacramento Blvd, Chicago', contact: '(312) 798-3400', desc: 'Employment training and job placement for people experiencing poverty and homelessness. West side focus.' },
      { name: 'Neighborhood Housing Services Chicago', category: 'Housing', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', address: '1279 N Milwaukee Ave, Chicago', contact: '(773) 329-4010', desc: 'Affordable housing counseling, foreclosure prevention, and community development.' },
      { name: 'Heartland Health Centers', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: 'Multiple Chicago locations', contact: '(773) 564-5500', desc: 'Community health centers serving uninsured and underinsured patients. West side locations.' },
      { name: 'Chicago Cook Workforce Partnership', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: '69 W Washington St, Chicago', contact: '(312) 603-0200', desc: 'Workforce development and career services. American Job Centers throughout Chicago.' },
    ],
    history: 'Austin is Chicago\'s largest neighborhood by area. The Great Migration transformed it from a white working-class community to a majority Black neighborhood. The 1968 uprising following MLK\'s assassination devastated the commercial corridors. The HOLC redlining maps designated Austin as "hazardous," ensuring systematic mortgage denial and disinvestment. Today Austin has the highest vacancy rate on Chicago\'s west side.',
    irlLens: [
      { stage: 'ENCOUNTER', arabic: 'مُشَاهَدَة', title: 'Sensory Contact with Austin', content: 'Walking the Green Line corridor — the elevated train above, the vacant lots below, the Austin Town Hall, the community organizations working in the gaps. Austin is the largest neighborhood in Chicago. Its haal carries the weight of the entire west side.' },
      { stage: 'EXAMINATION', arabic: 'فَحْص', title: 'Three Questions of the Lens', content: 'What is the haal of Austin? 82% food desert, 48% vacancy — the legacy of the 1968 uprising and decades of disinvestment. What Names are visible in the organizations that remain? What Amanah is called for in the vacant lots?' },
      { stage: 'RECOGNITION', arabic: 'مَعْرِفَة', title: 'Names Visible in Austin', content: 'Ar-Razzaq — in the food bank mobile distributions. Al-Mughni — in Cara Collective\'s employment programs. Al-Wali — in NHS Chicago\'s housing counseling. Ash-Shafi — in Heartland Health Centers.' },
      { stage: 'UBOOR', arabic: 'عُبُور', title: 'Crossing Over — Austin Islah', content: 'The Amanah of Austin is to bridge the largest neighborhood in Chicago from its current haal to Islah. Every resource here is a form of Uboor. Fisabilillah.' },
    ],
    context: {
      nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable',
      ayat: 'إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ',
      ayatRef: 'Quran 16:90',
      ayatTrans: '"Indeed, Allah commands justice, excellence, and giving to relatives."',
      intro: 'Austin is Chicago\'s largest neighborhood — and one of its most disinvested. The 1968 uprising following MLK\'s assassination devastated the commercial corridors. The HOLC redlining maps ensured that the devastation would not be repaired.',
      body: 'The food desert that covers 82% of Austin maps onto the 1940 HOLC redlining boundary. The 48% vacancy rate is the legacy of the 1968 uprising and decades of disinvestment. And yet — community organizations remain. The Amanah of Austin is to bridge the largest neighborhood in Chicago from its current haal to Islah. Fisabilillah.',
    },
  },
  // ── WEST GARFIELD ─────────────────────────────────────────────────────────────
  {
    id: 'west-garfield',
    name: 'West Garfield Park',
    shortName: 'W. GARFIELD',
    severity: 'CRITICAL',
    indicators: { poverty: 44, medianIncome: 20000, foodDesert: 90, unemployment: 30, uninsured: 42, vacancy: 52 },
    masajid: ['Masjid Al-Nur (West Garfield)'],
    anchors: ['Garfield Park Conservatory', 'Malcolm X College', 'West Garfield Park Community Organization'],
    openLetterQuote: 'The Conservatory holds the most beautiful plants in Chicago — surrounded by the most severe food desert. This is the haal.',
    pivotalMoments: [
      { year: '1940s–60s', event: 'Great Migration brings Black families; redlining ensures disinvestment' },
      { year: '1968', event: 'West side uprising following MLK assassination devastates commercial corridors' },
    ],
    disparities: [
      { name: 'Food Desert', value: '90%', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', islahCall: 'Extreme food desert adjacent to the Garfield Park Conservatory. Bridge to food banks and community gardens.' },
      { name: 'Uninsured', value: '42%', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', islahCall: 'Health desert compounds food desert. Bridge to Erie Family Health Centers and Heartland Health Centers.' },
    ],
    resources: [
      { name: 'IMAN Health Clinic', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: '2744 W 63rd St, Chicago', contact: '(773) 434-4626', desc: 'Free community health clinic. Serves uninsured patients across Chicago\'s south and west sides.' },
      { name: 'Greater Chicago Food Depository', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '4100 W Ann Lurie Pl, Chicago', contact: '(773) 247-3663', desc: 'Chicago\'s food bank. Mobile distribution in West Garfield food desert areas.' },
      { name: 'Erie Family Health Centers', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: 'Multiple Chicago locations', contact: '(312) 666-3494', desc: 'Federally qualified health center. Sliding scale fees. West side locations.' },
      { name: 'Chicago Cook Workforce Partnership', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: '69 W Washington St, Chicago', contact: '(312) 603-0200', desc: 'Workforce development and career services. American Job Centers throughout Chicago.' },
    ],
    history: 'West Garfield Park contains one of the most beautiful botanical gardens in the world — the Garfield Park Conservatory. It also contains one of Chicago\'s most severe food deserts. This juxtaposition encodes the haal: natural abundance surrounded by structural deprivation.',
    irlLens: [
      { stage: 'ENCOUNTER', arabic: 'مُشَاهَدَة', title: 'Sensory Contact with West Garfield', content: 'Standing at the Garfield Park Conservatory — tropical plants, humid air, the smell of earth and growth. Step outside: 90% food desert. The Conservatory holds the most beautiful plants in Chicago, surrounded by the most severe food desert. This is the haal.' },
      { stage: 'EXAMINATION', arabic: 'فَحْص', title: 'Three Questions of the Lens', content: 'What is the haal of West Garfield? The Conservatory — abundance — surrounded by the food desert — absence. 44% poverty, 42% uninsured, 52% vacancy. What Names are visible in the Conservatory? What Names are called for in the food desert?' },
      { stage: 'RECOGNITION', arabic: 'مَعْرِفَة', title: 'Names Visible in West Garfield', content: 'Ar-Razzaq — in the Conservatory\'s plants, in the food bank mobile distributions. Ash-Shafi — in Erie Family Health Centers, in IMAN\'s clinic. Al-Mughni — in the workforce development programs.' },
      { stage: 'UBOOR', arabic: 'عُبُور', title: 'Crossing Over — West Garfield Islah', content: 'The Amanah of West Garfield is to bridge the Conservatory\'s abundance to the food desert\'s absence. Every resource here is a form of Uboor. Fisabilillah.' },
    ],
    context: {
      nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider',
      ayat: 'وَمَا مِن دَابَّةٍ فِي الْأَرْضِ إِلَّا عَلَى اللَّهِ رِزْقُهَا',
      ayatRef: 'Quran 11:6',
      ayatTrans: '"There is no creature on earth whose provision is not guaranteed by Allah."',
      intro: 'West Garfield Park contains one of the most beautiful botanical gardens in the world — the Garfield Park Conservatory. It also contains one of Chicago\'s most severe food deserts. This juxtaposition is the haal.',
      body: 'The 90% food desert in West Garfield maps onto the 1940 HOLC redlining boundary and the devastation of the 1968 uprising. The Conservatory — built in 1906 — holds tropical plants from around the world while the surrounding community lacks access to fresh produce. The Amanah of West Garfield is to bridge the Conservatory\'s abundance to the food desert\'s absence. Fisabilillah.',
    },
  },
  // ── NORTH LAWNDALE ────────────────────────────────────────────────────────────
  {
    id: 'north-lawndale',
    name: 'North Lawndale',
    shortName: 'N. LAWNDALE',
    severity: 'HIGH',
    indicators: { poverty: 36, medianIncome: 24000, foodDesert: 75, unemployment: 24, uninsured: 35, vacancy: 42 },
    masajid: ['Mosque Maryam (Nation of Islam, nearby)', 'North Lawndale Islamic Center'],
    anchors: ['Lawndale Christian Health Center', 'North Lawndale Employment Network', 'Sweet Home Chicago'],
    openLetterQuote: 'North Lawndale was where Dr. King chose to live during the Chicago Freedom Movement. The Amanah of this place is immense.',
    pivotalMoments: [
      { year: '1966', event: 'Dr. Martin Luther King Jr. moves to North Lawndale apartment to experience Chicago\'s housing conditions' },
      { year: '1968', event: 'Uprising following MLK assassination devastates commercial corridors' },
      { year: '1970s', event: 'Sears Roebuck closes headquarters — 10,000 jobs lost; neighborhood devastated' },
    ],
    disparities: [
      { name: 'Redlining Legacy', value: 'Direct 1940 HOLC boundary', nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable', islahCall: 'The redlining boundary is still visible in current disparities. Bridge to advocacy and community development.' },
      { name: 'Food Desert', value: '75%', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', islahCall: 'Provision is blocked. Bridge to food banks and community gardens.' },
    ],
    resources: [
      { name: 'North Lawndale Employment Network', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: '2928 W Roosevelt Rd, Chicago', contact: '(773) 579-1100', desc: 'Employment training, job placement, and economic development for North Lawndale residents.' },
      { name: 'Lawndale Christian Health Center', category: 'Health', nameOfAllah: 'ASH-SHAFI', arabicName: 'الشَّافِي', meaning: 'The Healer', address: '3860 W Ogden Ave, Chicago', contact: '(773) 854-6300', desc: 'Community health center serving uninsured and underinsured patients in North Lawndale.' },
      { name: 'CAIR Chicago', category: 'Justice', nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable', address: '17 N State St, Chicago', contact: '(312) 212-1520', desc: 'Civil rights advocacy, legal services, and community empowerment for Chicago\'s Muslim community.' },
      { name: 'Greater Chicago Food Depository', category: 'Food', nameOfAllah: 'AR-RAZZAQ', arabicName: 'الرَّزَّاق', meaning: 'The Ever-Provider', address: '4100 W Ann Lurie Pl, Chicago', contact: '(773) 247-3663', desc: 'Chicago\'s food bank. Mobile distribution in North Lawndale food desert areas.' },
    ],
    history: 'North Lawndale was where Dr. Martin Luther King Jr. chose to live during the 1966 Chicago Freedom Movement — to experience firsthand the housing conditions facing Black Chicagoans. The closure of Sears Roebuck\'s headquarters in the 1970s eliminated 10,000 jobs and devastated the local economy.',
    irlLens: [
      { stage: 'ENCOUNTER', arabic: 'مُشَاهَدَة', title: 'Sensory Contact with North Lawndale', content: 'Walking Roosevelt Road — the corridor where Dr. King lived in 1966. The apartment where he chose to experience Chicago\'s housing conditions. The empty Sears headquarters. The North Lawndale Employment Network. What does it mean that Dr. King chose this place?' },
      { stage: 'EXAMINATION', arabic: 'فَحْص', title: 'Three Questions of the Lens', content: 'What is the haal of North Lawndale? Dr. King chose to live here to bear witness. The Sears closure eliminated 10,000 jobs. The redlining boundary is still visible in the food desert map. What Names are called for in this place?' },
      { stage: 'RECOGNITION', arabic: 'مَعْرِفَة', title: 'Names Visible in North Lawndale', content: 'Al-Muqsit — in Dr. King\'s witness, in CAIR Chicago\'s advocacy. Al-Mughni — in the North Lawndale Employment Network. Ash-Shafi — in Lawndale Christian Health Center. Ar-Razzaq — in the food bank distributions.' },
      { stage: 'UBOOR', arabic: 'عُبُور', title: 'Crossing Over — North Lawndale Islah', content: 'The Amanah of North Lawndale is to continue the witness that Dr. King began — to see the haal clearly and to bridge it to Islah. Fisabilillah.' },
    ],
    context: {
      nameOfAllah: 'AL-MUQSIT', arabicName: 'الْمُقْسِط', meaning: 'The Equitable',
      ayat: 'كُونُوا قَوَّامِينَ بِالْقِسْطِ شُهَدَاءَ لِلَّهِ',
      ayatRef: 'Quran 4:135',
      ayatTrans: '"Be persistently standing firm in justice, witnesses for Allah."',
      intro: 'North Lawndale is where Dr. Martin Luther King Jr. chose to live during the 1966 Chicago Freedom Movement — to bear witness to the housing conditions facing Black Chicagoans.',
      body: 'The 1940 HOLC redlining maps designated North Lawndale as "hazardous." The closure of Sears Roebuck\'s headquarters in the 1970s eliminated 10,000 jobs. The 1968 uprising devastated the commercial corridors. And yet — the North Lawndale Employment Network, Lawndale Christian Health Center, and community organizations continue the work of Islah. The Amanah of North Lawndale is to continue the witness that Dr. King began. Fisabilillah.',
    },
  },
  // ── CHATHAM ───────────────────────────────────────────────────────────────────
  {
    id: 'chatham',
    name: 'Chatham',
    shortName: 'CHATHAM',
    severity: 'MODERATE',
    indicators: { poverty: 22, medianIncome: 38000, foodDesert: 55, unemployment: 15, uninsured: 22, vacancy: 22 },
    masajid: ['Chatham Islamic Center', 'Masjid Al-Haqq'],
    anchors: ['Chatham Business Association', 'Chatham Park Village', 'Mahalia Jackson\'s former home'],
    openLetterQuote: 'Chatham is Black Chicago\'s middle-class anchor — the Amanah is to hold what was built against the erosion.',
    pivotalMoments: [
      { year: '1950s–60s', event: 'Chatham develops as Black middle-class neighborhood — one of Chicago\'s most stable' },
      { year: '1960s', event: 'Mahalia Jackson, Nat King Cole, and other Black cultural figures make Chatham home' },
    ],
    disparities: [
      { name: 'Black Middle-Class Erosion', value: 'Median income declining', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', islahCall: 'Chatham\'s Black middle class is eroding. Bridge to wealth-building and homeownership preservation programs.' },
    ],
    resources: [
      { name: 'Muslim Community Center Chicago', category: 'Community', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', address: '4380 N Elston Ave, Chicago', contact: '(773) 725-9047', desc: 'Comprehensive Muslim community center. Education, social services, youth programs, and community support.' },
      { name: 'Neighborhood Housing Services Chicago', category: 'Housing', nameOfAllah: 'AL-WALI', arabicName: 'الْوَلِيّ', meaning: 'The Protecting Friend', address: '1279 N Milwaukee Ave, Chicago', contact: '(773) 329-4010', desc: 'Affordable housing counseling, foreclosure prevention, and homeownership preservation.' },
      { name: 'IMAN — Inner-City Muslim Action Network', category: 'Community', nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving', address: '2744 W 63rd St, Chicago', contact: '(773) 434-4626', desc: 'Comprehensive community development serving Chicago\'s south side including Chatham.' },
      { name: 'Chicago Urban League', category: 'Income', nameOfAllah: 'AL-MUGHNI', arabicName: 'الْمُغْنِي', meaning: 'The Enricher', address: '4510 S Michigan Ave, Chicago', contact: '(773) 285-5800', desc: 'Economic empowerment, workforce development, and entrepreneurship programs.' },
    ],
    history: 'Chatham developed as one of Chicago\'s most stable Black middle-class neighborhoods in the 1950s–60s. Mahalia Jackson, Nat King Cole, and other Black cultural figures made Chatham home. The neighborhood has maintained relative stability compared to surrounding areas but faces increasing pressure from middle-class erosion.',
    irlLens: [
      { stage: 'ENCOUNTER', arabic: 'مُشَاهَدَة', title: 'Sensory Contact with Chatham', content: 'Walking 79th Street — the commercial corridor, the well-maintained homes, the Chatham Business Association. Chatham is Black Chicago\'s middle-class anchor. What does stability look like in the context of surrounding disinvestment?' },
      { stage: 'EXAMINATION', arabic: 'فَحْص', title: 'Three Questions of the Lens', content: 'What is the haal of Chatham? Relative stability — but the Black middle class is eroding. Mahalia Jackson lived here. What Names are visible in what was built? What Amanah is called for in preserving it?' },
      { stage: 'RECOGNITION', arabic: 'مَعْرِفَة', title: 'Names Visible in Chatham', content: 'Al-Wadud — in the community that built and maintained Chatham. Al-Mughni — in the economic empowerment programs. Al-Wali — in the homeownership preservation efforts.' },
      { stage: 'UBOOR', arabic: 'عُبُور', title: 'Crossing Over — Chatham Islah', content: 'The Amanah of Chatham is to hold what was built — to preserve the Black middle-class anchor against erosion. Fisabilillah.' },
    ],
    context: {
      nameOfAllah: 'AL-WADUD', arabicName: 'الْوَدُود', meaning: 'The Loving',
      ayat: 'وَاعْتَصِمُوا بِحَبْلِ اللَّهِ جَمِيعًا وَلَا تَفَرَّقُوا',
      ayatRef: 'Quran 3:103',
      ayatTrans: '"And hold firmly to the rope of Allah all together and do not become divided."',
      intro: 'Chatham is Black Chicago\'s middle-class anchor — built in the 1950s–60s by Black families who created stability in the face of surrounding disinvestment.',
      body: 'Mahalia Jackson, Nat King Cole, and other Black cultural figures made Chatham home. The neighborhood has maintained relative stability compared to surrounding areas. But the Black middle class is eroding — economic pressures and gentrification from the north are building. The Amanah of Chatham is to hold what was built, to preserve the anchor, and to extend its stability to surrounding communities. Fisabilillah.',
    },
  },
];

const PANEL_TABS = ['RESOURCES', 'CONTEXT'] as const;
type PanelTab = typeof PANEL_TABS[number];

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'rgba(255,255,255,1)',
  HIGH: 'rgba(255,255,255,0.6)',
  MODERATE: 'rgba(255,255,255,0.35)',
};

export default function CaretakerMataWidget() {
  const [selectedWardId, setSelectedWardId] = useState<string>('bronzeville');
  const [activeTab, setActiveTab] = useState<PanelTab>('RESOURCES');
  const [resourceFilter, setResourceFilter] = useState<string>('All');

  const selectedWard = useMemo(() => CHICAGO_WARDS.find(w => w.id === selectedWardId)!, [selectedWardId]);

  const resourceCategories = useMemo(() => {
    const cats = new Set(selectedWard.resources.map(r => r.category));
    return ['All', ...Array.from(cats)];
  }, [selectedWard]);

  const filteredResources = useMemo(() => {
    if (resourceFilter === 'All') return selectedWard.resources;
    return selectedWard.resources.filter(r => r.category === resourceFilter);
  }, [selectedWard, resourceFilter]);

  // Group resources by category for the Resources tab
  const resourcesByCategory = useMemo(() => {
    const groups: Record<string, ResourceItem[]> = {};
    filteredResources.forEach(r => {
      if (!groups[r.category]) groups[r.category] = [];
      groups[r.category].push(r);
    });
    return groups;
  }, [filteredResources]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Amiri:wght@400;700&display=swap');

        .cmw-wrap {
          background: #fff;
          border: 1px solid rgba(30,58,138,0.1);
          font-family: monospace;
        }

        /* ── Opening Premise ── */
        .cmw-premise {
          padding: 28px 32px;
          border-bottom: 1px solid rgba(30,58,138,0.07);
          background: rgba(30,58,138,0.02);
        }
        .cmw-premise-label {
          font-size: 10px;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 8px;
        }
        .cmw-premise-title {
          font-size: clamp(16px, 2.5vw, 22px);
          font-weight: 900;
          color: #0f172a;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 6px;
        }
        .cmw-premise-ar {
          font-family: 'Amiri', serif;
          font-size: 16px;
          color: rgba(30,58,138,0.25);
          direction: rtl;
          display: block;
          margin-bottom: 8px;
        }
        .cmw-premise-body {
          font-size: 13px;
          color: rgba(15,23,42,0.55);
          line-height: 1.8;
          max-width: 700px;
        }

        /* ── Ecosystem header ── */
        .cmw-eco-header {
          padding: 24px 32px 20px;
          border-bottom: 1px solid rgba(30,58,138,0.07);
        }
        .cmw-eco-label {
          font-size: 10px;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 6px;
        }
        .cmw-eco-title {
          font-size: clamp(18px, 3vw, 30px);
          font-weight: 900;
          color: #0f172a;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .cmw-eco-sub {
          font-size: 13px;
          color: rgba(15,23,42,0.5);
          line-height: 1.6;
          max-width: 600px;
        }

        /* ── Ward grid ── */
        .cmw-ward-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
          gap: 1px;
          background: rgba(30,58,138,0.08);
          border-bottom: 1px solid rgba(30,58,138,0.08);
        }
        .cmw-ward-btn {
          background: #fff;
          border: none;
          padding: 16px 10px;
          cursor: pointer;
          transition: background 0.2s;
          text-align: center;
          position: relative;
        }
        .cmw-ward-btn:hover { background: rgba(30,58,138,0.04); }
        .cmw-ward-btn.active { background: #1e3a8a; }
        .cmw-ward-btn.primary-ward {
          border-top: 2px solid rgba(212,175,55,0.5);
        }
        .cmw-ward-btn.active.primary-ward {
          border-top-color: rgba(212,175,55,0.8);
        }
        .cmw-ward-name {
          font-size: 9px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(15,23,42,0.55);
          display: block;
          margin-bottom: 4px;
          font-weight: 700;
        }
        .cmw-ward-btn.active .cmw-ward-name { color: rgba(255,255,255,0.9); }
        .cmw-ward-severity {
          font-size: 7px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }
        .cmw-ward-btn.active .cmw-ward-severity { color: rgba(255,255,255,0.5); }
        .cmw-ward-stat {
          font-size: 16px;
          font-weight: 900;
          color: rgba(15,23,42,0.6);
          display: block;
          margin-top: 4px;
        }
        .cmw-ward-btn.active .cmw-ward-stat { color: #fff; }
        .cmw-ward-primary-badge {
          position: absolute;
          top: 4px; right: 4px;
          font-size: 6px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(212,175,55,0.7);
          background: rgba(212,175,55,0.08);
          padding: 2px 4px;
          border: 1px solid rgba(212,175,55,0.2);
        }
        .cmw-ward-btn.active .cmw-ward-primary-badge {
          color: rgba(212,175,55,0.9);
          background: rgba(212,175,55,0.12);
        }

        /* ── Detail panel ── */
        .cmw-detail {
          display: flex;
          flex-direction: column;
        }
        .cmw-ward-header {
          padding: 20px 24px;
          border-bottom: 1px solid rgba(30,58,138,0.07);
          background: rgba(30,58,138,0.02);
        }
        .cmw-ward-header-label {
          font-size: 10px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 4px;
        }
        .cmw-ward-header-name {
          font-size: clamp(18px, 3vw, 26px);
          font-weight: 900;
          color: #0f172a;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .cmw-ward-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }
        .cmw-ward-stat-item {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .cmw-ward-stat-val {
          font-size: 20px;
          font-weight: 900;
          color: #1e3a8a;
          line-height: 1;
        }
        .cmw-ward-stat-lbl {
          font-size: 9px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(15,23,42,0.4);
        }

        /* ── Tabs ── */
        .cmw-tabs {
          display: flex;
          border-bottom: 1px solid rgba(30,58,138,0.08);
          overflow-x: auto;
          scrollbar-width: none;
        }
        .cmw-tabs::-webkit-scrollbar { display: none; }
        .cmw-tab {
          font-size: 11px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          padding: 14px 22px;
          background: none;
          border: none;
          border-right: 1px solid rgba(30,58,138,0.07);
          cursor: pointer;
          transition: all 0.2s;
          color: rgba(15,23,42,0.5);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .cmw-tab:hover { color: #1e3a8a; background: rgba(30,58,138,0.03); }
        .cmw-tab.active { background: #1e3a8a; color: #fff; }

        /* ── Tab content ── */
        .cmw-tab-content {
          padding: 24px;
          max-height: 640px;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: rgba(30,58,138,0.15) transparent;
        }
        .cmw-tab-content::-webkit-scrollbar { width: 4px; }
        .cmw-tab-content::-webkit-scrollbar-track { background: transparent; }
        .cmw-tab-content::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.15); }

        /* ── RESOURCES tab ── */
        .cmw-res-filter {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 20px;
        }
        .cmw-res-filter-btn {
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          padding: 7px 14px;
          background: none;
          border: 1px solid rgba(30,58,138,0.12);
          color: rgba(15,23,42,0.5);
          cursor: pointer;
          transition: all 0.2s;
        }
        .cmw-res-filter-btn:hover { border-color: rgba(30,58,138,0.3); color: #1e3a8a; }
        .cmw-res-filter-btn.active { background: #1e3a8a; color: #fff; border-color: #1e3a8a; }

        /* Resource category block */
        .cmw-res-cat-block {
          margin-bottom: 28px;
        }
        .cmw-res-cat-header {
          padding: 18px 20px;
          border: 1px solid rgba(30,58,138,0.1);
          background: rgba(30,58,138,0.02);
          margin-bottom: 1px;
        }
        .cmw-res-cat-allah {
          display: flex;
          align-items: baseline;
          gap: 12px;
          margin-bottom: 8px;
        }
        .cmw-res-cat-allah-ar {
          font-family: 'Amiri', serif;
          font-size: 26px;
          color: rgba(30,58,138,0.35);
        }
        .cmw-res-cat-allah-en {
          font-size: 11px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.6);
        }
        .cmw-res-cat-allah-meaning {
          font-size: 10px;
          color: rgba(15,23,42,0.4);
          font-style: italic;
        }
        .cmw-res-cat-ayat-ar {
          font-family: 'Amiri', serif;
          font-size: 17px;
          color: rgba(30,58,138,0.3);
          direction: rtl;
          display: block;
          margin-bottom: 6px;
        }
        .cmw-res-cat-ayat-trans {
          font-size: 12px;
          color: rgba(15,23,42,0.45);
          font-style: italic;
          line-height: 1.6;
          margin-bottom: 4px;
        }
        .cmw-res-cat-ayat-ref {
          font-size: 10px;
          color: rgba(30,58,138,0.35);
          letter-spacing: 0.1em;
        }
        .cmw-res-cat-intro {
          font-size: 12px;
          color: rgba(15,23,42,0.5);
          line-height: 1.8;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(30,58,138,0.06);
        }

        /* Resource items */
        .cmw-res-item {
          border: 1px solid rgba(30,58,138,0.07);
          border-top: none;
          padding: 16px 20px;
          background: #fff;
          transition: background 0.2s;
        }
        .cmw-res-item:hover { background: rgba(30,58,138,0.02); }
        .cmw-res-item-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 6px;
        }
        .cmw-res-item-name {
          font-size: 13px;
          font-weight: 900;
          color: #0f172a;
          text-transform: uppercase;
          line-height: 1.3;
        }
        .cmw-res-item-allah {
          flex-shrink: 0;
          text-align: right;
        }
        .cmw-res-item-allah-ar {
          font-family: 'Amiri', serif;
          font-size: 16px;
          color: rgba(30,58,138,0.3);
          display: block;
        }
        .cmw-res-item-allah-en {
          font-size: 8px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.3);
        }
        .cmw-res-item-desc {
          font-size: 13px;
          color: rgba(15,23,42,0.6);
          line-height: 1.7;
          margin-bottom: 8px;
        }
        .cmw-res-item-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .cmw-res-item-addr {
          font-size: 11px;
          color: rgba(15,23,42,0.4);
          letter-spacing: 0.05em;
        }
        .cmw-res-item-contact {
          font-size: 11px;
          color: rgba(30,58,138,0.5);
        }

        /* ── CONTEXT tab ── */
        .cmw-ctx-allah {
          display: flex;
          align-items: baseline;
          gap: 14px;
          margin-bottom: 14px;
          padding-bottom: 14px;
          border-bottom: 1px solid rgba(30,58,138,0.08);
        }
        .cmw-ctx-allah-ar {
          font-family: 'Amiri', serif;
          font-size: 32px;
          color: rgba(30,58,138,0.35);
        }
        .cmw-ctx-allah-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .cmw-ctx-allah-en {
          font-size: 12px;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.6);
        }
        .cmw-ctx-allah-meaning {
          font-size: 11px;
          color: rgba(15,23,42,0.45);
          font-style: italic;
        }
        .cmw-ctx-ayat-block {
          padding: 16px 18px;
          border: 1px solid rgba(30,58,138,0.1);
          background: rgba(30,58,138,0.02);
          margin-bottom: 18px;
        }
        .cmw-ctx-ayat-ar {
          font-family: 'Amiri', serif;
          font-size: 20px;
          color: rgba(30,58,138,0.35);
          direction: rtl;
          display: block;
          margin-bottom: 8px;
        }
        .cmw-ctx-ayat-trans {
          font-size: 13px;
          color: rgba(15,23,42,0.5);
          font-style: italic;
          line-height: 1.6;
          margin-bottom: 4px;
        }
        .cmw-ctx-ayat-ref {
          font-size: 10px;
          color: rgba(30,58,138,0.35);
          letter-spacing: 0.1em;
        }
        .cmw-ctx-intro {
          font-size: 14px;
          color: rgba(15,23,42,0.65);
          line-height: 1.8;
          margin-bottom: 14px;
          font-style: italic;
        }
        .cmw-ctx-body {
          font-size: 13px;
          color: rgba(15,23,42,0.6);
          line-height: 1.9;
        }
        .cmw-ctx-masajid {
          margin-top: 18px;
          padding: 16px 18px;
          border: 1px solid rgba(30,58,138,0.08);
          background: rgba(30,58,138,0.02);
        }
        .cmw-ctx-masajid-label {
          font-size: 9px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 10px;
        }
        .cmw-ctx-masajid-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .cmw-ctx-masajid-item {
          font-size: 12px;
          color: rgba(15,23,42,0.6);
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .cmw-ctx-masajid-item::before {
          content: '⬡';
          font-size: 8px;
          color: rgba(30,58,138,0.3);
        }

        /* ── IRL Lens in Context ── */
        .cmw-irl-content {
          border: 1px solid rgba(30,58,138,0.1);
          padding: 18px;
          background: rgba(30,58,138,0.02);
        }
        .cmw-irl-content-stage {
          font-size: 9px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 4px;
        }
        .cmw-irl-content-ar {
          font-family: 'Amiri', serif;
          font-size: 22px;
          color: rgba(30,58,138,0.3);
          display: block;
          margin-bottom: 4px;
        }
        .cmw-irl-content-title {
          font-size: 13px;
          font-weight: 900;
          color: #0f172a;
          text-transform: uppercase;
          margin-bottom: 10px;
        }
        .cmw-irl-content-body {
          font-size: 13px;
          color: rgba(15,23,42,0.6);
          line-height: 1.8;
        }

        /* ── Section label ── */
        .cmw-section-label {
          font-size: 10px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 12px;
        }

        /* ── Disparity styles (kept for context) ── */
        .cmw-quote-block {
          padding: 16px 18px;
          border: 1px solid rgba(30,58,138,0.1);
          background: rgba(30,58,138,0.02);
          margin-bottom: 18px;
        }
        .cmw-quote-label {
          font-size: 9px;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: rgba(30,58,138,0.35);
          margin-bottom: 6px;
        }
        .cmw-quote-text {
          font-size: 13px;
          color: rgba(15,23,42,0.6);
          font-style: italic;
          line-height: 1.7;
        }
        .cmw-disparity-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 18px;
        }
        .cmw-disparity-item {
          border: 1px solid rgba(30,58,138,0.08);
          padding: 14px 16px;
          background: #fff;
        }
        .cmw-disparity-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 6px;
        }
        .cmw-disparity-name {
          font-size: 12px;
          font-weight: 900;
          color: #0f172a;
          text-transform: uppercase;
        }
        .cmw-disparity-val {
          font-size: 11px;
          color: rgba(15,23,42,0.5);
        }
        .cmw-disparity-allah {
          text-align: right;
          flex-shrink: 0;
        }
        .cmw-disparity-allah-ar {
          font-family: 'Amiri', serif;
          font-size: 16px;
          color: rgba(30,58,138,0.4);
          display: block;
        }
        .cmw-disparity-allah-en {
          font-size: 8px;
          letter-spacing: 0.1em;
          color: rgba(30,58,138,0.35);
          text-transform: uppercase;
        }
        .cmw-disparity-islah {
          font-size: 12px;
          color: rgba(15,23,42,0.55);
          line-height: 1.6;
        }
        .cmw-pivotal-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .cmw-pivotal-item {
          display: flex;
          gap: 14px;
          border-left: 1px solid rgba(30,58,138,0.1);
          padding-left: 14px;
        }
        .cmw-pivotal-year {
          font-size: 10px;
          color: rgba(30,58,138,0.45);
          flex-shrink: 0;
          width: 80px;
        }
        .cmw-pivotal-event {
          font-size: 12px;
          color: rgba(15,23,42,0.6);
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .cmw-ward-grid { grid-template-columns: repeat(3, 1fr); }
          .cmw-tab-content { max-height: 500px; }
        }
      `}</style>

      <div className="cmw-wrap">

        {/* Opening Premise */}
        <div className="cmw-premise">
          <p className="cmw-premise-label">OPENING PREMISE — CITY RESOURCE SECTION</p>
          <p className="cmw-premise-title">&ldquo;The Whole Earth is a Masjid&rdquo;</p>
          <span className="cmw-premise-ar">جُعِلَتْ لِيَ الْأَرْضُ مَسْجِدًا وَطَهُورًا</span>
          <p className="cmw-premise-body">
            Every ward, every city, every locality is a place of prostration. The caretaker&apos;s work is not separate from worship — it is worship. The city is not outside the Masjid — the city is the Masjid. Every disparity mapped here is a call to Islah. Every resource listed is a form of Amanah. Every caretaker using this tool is fulfilling a sacred trust — fisabilillah.
          </p>
        </div>

        {/* Ecosystem Header */}
        <div className="cmw-eco-header">
          <p className="cmw-eco-label">THE HAAL PROJECT — MASJID AL-IHSAN SUBHAAL · CHICAGO</p>
          <h2 className="cmw-eco-title">MUSLIM RESOURCE GUIDE — CHICAGO</h2>
          <p className="cmw-eco-sub">
            Haqq · Amanah · Islah — A living platform connecting caretakers to the resources, ayat, and Names of Allah that bridge disparity to provision. Bronzeville is the primary ward. Each ward is a haal. Each disparity is a call to Islah. fisabilillah.
          </p>
        </div>

        {/* Ward Grid */}
        <div className="cmw-ward-grid">
          {CHICAGO_WARDS.map(ward => (
            <button
              key={ward.id}
              className={`cmw-ward-btn ${selectedWardId === ward.id ? 'active' : ''} ${ward.id === 'bronzeville' ? 'primary-ward' : ''}`}
              onClick={() => { setSelectedWardId(ward.id); setActiveTab('RESOURCES'); setResourceFilter('All'); }}
            >
              {ward.id === 'bronzeville' && (
                <span className="cmw-ward-primary-badge">Primary</span>
              )}
              <span className="cmw-ward-name">{ward.shortName}</span>
              <span className="cmw-ward-stat">{ward.indicators.poverty}%</span>
              <span className="cmw-ward-severity" style={{ color: selectedWardId === ward.id ? 'rgba(0,0,0,0.4)' : SEVERITY_COLORS[ward.severity] }}>
                {ward.severity.slice(0, 4)}
              </span>
            </button>
          ))}
        </div>

        {/* Detail Panel */}
        <div className="cmw-detail">

          {/* Ward Header */}
          <div className="cmw-ward-header">
            <p className="cmw-ward-header-label">{selectedWard.severity} — CHICAGO · {selectedWard.id === 'bronzeville' ? 'PRIMARY WARD' : 'WARD'}</p>
            <h3 className="cmw-ward-header-name">{selectedWard.name}</h3>
            <div className="cmw-ward-stats">
              <div className="cmw-ward-stat-item">
                <span className="cmw-ward-stat-val">{selectedWard.indicators.poverty}%</span>
                <span className="cmw-ward-stat-lbl">Poverty</span>
              </div>
              <div className="cmw-ward-stat-item">
                <span className="cmw-ward-stat-val">{selectedWard.indicators.foodDesert}%</span>
                <span className="cmw-ward-stat-lbl">Food Desert</span>
              </div>
              <div className="cmw-ward-stat-item">
                <span className="cmw-ward-stat-val">{selectedWard.indicators.unemployment}%</span>
                <span className="cmw-ward-stat-lbl">Unemployment</span>
              </div>
              <div className="cmw-ward-stat-item">
                <span className="cmw-ward-stat-val">{selectedWard.indicators.uninsured}%</span>
                <span className="cmw-ward-stat-lbl">Uninsured</span>
              </div>
              <div className="cmw-ward-stat-item">
                <span className="cmw-ward-stat-val">${(selectedWard.indicators.medianIncome / 1000).toFixed(0)}K</span>
                <span className="cmw-ward-stat-lbl">Median Income</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="cmw-tabs">
            {PANEL_TABS.map(tab => (
              <button
                key={tab}
                className={`cmw-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="cmw-tab-content">

            {/* ── RESOURCES TAB ── */}
            {activeTab === 'RESOURCES' && (
              <div>
                {/* Category filter */}
                <div className="cmw-res-filter">
                  {resourceCategories.map(cat => (
                    <button
                      key={cat}
                      className={`cmw-res-filter-btn ${resourceFilter === cat ? 'active' : ''}`}
                      onClick={() => setResourceFilter(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* Resources grouped by category with full Haal Project format */}
                {Object.entries(resourcesByCategory).map(([category, items]) => {
                  const catData = RESOURCE_CATEGORIES[category];
                  return (
                    <div key={category} className="cmw-res-cat-block">
                      {/* Category header: Name of Allah + Ayah + Intro */}
                      {catData && (
                        <div className="cmw-res-cat-header">
                          <div className="cmw-res-cat-allah">
                            <span className="cmw-res-cat-allah-ar">{catData.arabicName}</span>
                            <div>
                              <span className="cmw-res-cat-allah-en">{catData.nameOfAllah}</span>
                              <span className="cmw-res-cat-allah-meaning"> — {catData.meaning}</span>
                            </div>
                          </div>
                          <span className="cmw-res-cat-ayat-ar">{catData.ayat}</span>
                          <p className="cmw-res-cat-ayat-trans">{catData.ayatTrans}</p>
                          <p className="cmw-res-cat-ayat-ref">{catData.ayatRef}</p>
                          <p className="cmw-res-cat-intro">{catData.intro}</p>
                        </div>
                      )}

                      {/* Resource items */}
                      {items.map((resource, i) => (
                        <div key={i} className="cmw-res-item">
                          <div className="cmw-res-item-top">
                            <p className="cmw-res-item-name">{resource.name}</p>
                            <div className="cmw-res-item-allah">
                              <span className="cmw-res-item-allah-ar">{resource.arabicName}</span>
                              <span className="cmw-res-item-allah-en">{resource.nameOfAllah}</span>
                            </div>
                          </div>
                          <p className="cmw-res-item-desc">{resource.desc}</p>
                          <div className="cmw-res-item-meta">
                            {resource.address && <span className="cmw-res-item-addr">📍 {resource.address}</span>}
                            {resource.contact && <span className="cmw-res-item-contact">◈ {resource.contact}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── CONTEXT TAB (merged Uboor context + IRL Lens) ── */}
            {activeTab === 'CONTEXT' && (
              <div>
                <div className="cmw-ctx-allah">
                  <span className="cmw-ctx-allah-ar">{selectedWard.context.arabicName}</span>
                  <div className="cmw-ctx-allah-info">
                    <span className="cmw-ctx-allah-en">{selectedWard.context.nameOfAllah}</span>
                    <span className="cmw-ctx-allah-meaning">{selectedWard.context.meaning}</span>
                  </div>
                </div>

                <div className="cmw-ctx-ayat-block">
                  <span className="cmw-ctx-ayat-ar">{selectedWard.context.ayat}</span>
                  <p className="cmw-ctx-ayat-trans">{selectedWard.context.ayatTrans}</p>
                  <p className="cmw-ctx-ayat-ref">{selectedWard.context.ayatRef}</p>
                </div>

                <p className="cmw-ctx-intro">{selectedWard.context.intro}</p>
                <p className="cmw-ctx-body">{selectedWard.context.body}</p>

                <div className="cmw-ctx-masajid">
                  <p className="cmw-ctx-masajid-label">Masajid & Anchors</p>
                  <div className="cmw-ctx-masajid-list">
                    {[...selectedWard.masajid, ...selectedWard.anchors].map((item, i) => (
                      <span key={i} className="cmw-ctx-masajid-item">{item}</span>
                    ))}
                  </div>
                </div>

                {/* IRL Lens Journey — merged into Context */}
                {selectedWard.irlLens.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <p className="cmw-section-label" style={{ marginBottom: 12 }}>Inner Reality Lens — Caretaker&apos;s Journey</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {selectedWard.irlLens.map((step, i) => (
                        <div key={i} className="cmw-irl-content">
                          <p className="cmw-irl-content-stage">{step.stage}</p>
                          <span className="cmw-irl-content-ar">{step.arabic}</span>
                          <p className="cmw-irl-content-title">{step.title}</p>
                          <p className="cmw-irl-content-body">{step.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
